import { jsxs, jsx } from "react/jsx-runtime";
import * as React from "react";
import styled, { css } from "styled-components";
import { colors, cbSemanticCssVars, typographyCss } from "@creditornot/cb-ingredients";
import { createCssVars, createCssVarDefinitions, createThemedCssVarDefinitions } from "@creditornot/cb-utils";
const WIDTH_RATIO = 1.8;
const KNOB_SIZE_RATIO = 1 / 1.2;
const cssVars = createCssVars(["height", "borderWidth", "focusIndicatorWidth", "knobSize"]);
const TRANSITION_DURATION = "200ms";
const TRANSITION_TIMING_FUNCTION = "cubic-bezier(.4, .4, .2, 1)";
const colorsConfig = {
  borderUnchecked: {
    light: colors.pepper12,
    dark: colors.salt8
  },
  borderUncheckedDisabled: {
    light: colors.pepper12,
    dark: colors.salt8
  },
  borderChecked: colors.wolt100,
  borderCheckedDisabled: {
    light: colors.wolt8,
    dark: colors.wolt20
  },
  bgUnchecked: {
    light: colors.pepper40,
    dark: colors.salt20
  },
  bgUncheckedHover: {
    light: colors.pepper32,
    dark: colors.salt28
  },
  bgUncheckedActive: {
    light: colors.pepper24,
    dark: colors.salt24
  },
  bgUncheckedDisabled: {
    light: colors.pepper8,
    dark: colors.salt16
  },
  bgChecked: colors.wolt100,
  bgCheckedHover: colors.wolt92,
  bgCheckedActive: colors.wolt84,
  bgCheckedDisabled: {
    light: colors.wolt40,
    dark: colors.wolt60
  }
};
const colorCssVars$1 = createCssVars([
  "borderUnchecked",
  "borderUncheckedDisabled",
  "borderChecked",
  "borderCheckedDisabled",
  "bgUnchecked",
  "bgUncheckedHover",
  "bgUncheckedActive",
  "bgUncheckedDisabled",
  "bgChecked",
  "bgCheckedHover",
  "bgCheckedActive",
  "bgCheckedDisabled"
]);
const FocusIndicator = styled.div`
  position: absolute;
  top: -1px;
  left: -1px;
  right: -1px;
  bottom: -1px;
  z-index: 1;
  border-radius: calc(${cssVars.height} / 2);
`;
const knobDefaultPosition = `calc((${cssVars.height} - ${cssVars.knobSize} - 2px) / 2)`;
const BG = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  border-radius: calc(${cssVars.height} / 2);
  border-style: solid;
  border-width: ${cssVars.borderWidth};
  border-color: ${colorCssVars$1.borderUnchecked};
  background-color: ${colorCssVars$1.bgUnchecked};
  transition:
    background-color ${TRANSITION_DURATION} ${TRANSITION_TIMING_FUNCTION},
    border-color ${TRANSITION_DURATION} ${TRANSITION_TIMING_FUNCTION};

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    border-radius: 50%;
    background-color: ${colors.salt100};
    transition: left ${TRANSITION_DURATION} ${TRANSITION_TIMING_FUNCTION};
    width: ${cssVars.knobSize};
    height: ${cssVars.knobSize};
    left: ${knobDefaultPosition};
    top: ${knobDefaultPosition};
  }
`;
const Root$1 = styled.div`
  ${({ $cssVarOverrides }) => createCssVarDefinitions({
  height: "20px",
  borderWidth: "1px",
  focusIndicatorWidth: "2px",
  knobSize: `calc((var(--height) - 2px) * ${KNOB_SIZE_RATIO})`,
  ...$cssVarOverrides
})}

  ${createThemedCssVarDefinitions(colorsConfig)}

  box-sizing: border-box;
  flex-shrink: 0;
  position: relative;
  z-index: 0;
  width: calc(${cssVars.height} * ${WIDTH_RATIO});
  height: ${cssVars.height};
  border-radius: calc(${cssVars.height} / 2);
`;
const Input = styled.input`
  margin: 0;
  opacity: 0;
  outline: none;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  box-sizing: border-box;
  width: 100%;
  height: 100%;

  :focus-visible ~ ${FocusIndicator} {
    box-shadow: 0 0 0 ${cssVars.focusIndicatorWidth} ${cbSemanticCssVars.a11yFocusIndicator};
    outline: ${cssVars.focusIndicatorWidth} transparent solid;
  }

  &:disabled {
    cursor: not-allowed;
  }

  // unchecked
  &:not(:checked):not(:disabled) ~ ${BG} {
    border-color: ${colorCssVars$1.borderUnchecked};
    background-color: ${colorCssVars$1.bgUnchecked};
  }

  // unchecked hover
  &:not(:checked):not(:disabled):hover ~ ${BG} {
    border-color: ${colorCssVars$1.borderUnchecked};
    background-color: ${colorCssVars$1.bgUncheckedHover};
  }

  // unchecked active
  &:not(:checked):not(:disabled):active ~ ${BG} {
    border-color: ${colorCssVars$1.borderUnchecked};
    background-color: ${colorCssVars$1.bgUncheckedActive};
  }

  // unchecked disabled
  &:not(:checked):disabled ~ ${BG} {
    border-color: ${colorCssVars$1.borderUncheckedDisabled};
    background-color: ${colorCssVars$1.bgUncheckedDisabled};
  }

  // checked
  &:checked:not(:disabled) ~ ${BG} {
    border-color: ${colorCssVars$1.borderChecked};
    background-color: ${colorCssVars$1.bgChecked};
  }
  &:checked ~ ${BG}::after {
    left: 50%;
  }
  // checked hover
  &:checked:not(:disabled):hover ~ ${BG} {
    border-color: ${colorCssVars$1.borderChecked};
    background-color: ${colorCssVars$1.bgCheckedHover};
  }

  // checked active
  &:checked:not(:disabled):active ~ ${BG} {
    border-color: ${colorCssVars$1.borderChecked};
    background-color: ${colorCssVars$1.bgCheckedActive};
  }

  // checked disabled
  &:checked:disabled ~ ${BG} {
    border-color: ${colorCssVars$1.borderCheckedDisabled};
    background-color: ${colorCssVars$1.bgCheckedDisabled};
  }
`;
const InputSwitch = React.forwardRef(
  ({
    checked,
    className,
    cssVarOverrides,
    disabled = false,
    inputClassName,
    inputStyle,
    name,
    rootRef,
    style,
    ...rest
  }, ref) => /* @__PURE__ */ jsxs(
    Root$1,
    {
      $cssVarOverrides: cssVarOverrides,
      "aria-disabled": disabled,
      className,
      ref: rootRef,
      style,
      children: [
        /* @__PURE__ */ jsx(
          Input,
          {
            ...rest,
            checked,
            className: inputClassName,
            disabled,
            name,
            ref,
            style: inputStyle,
            type: "checkbox"
          }
        ),
        /* @__PURE__ */ jsx(BG, {}),
        /* @__PURE__ */ jsx(FocusIndicator, {})
      ]
    }
  )
);
InputSwitch.displayName = "InputSwitch";
const colorCssVars = createCssVars(["label", "labelPressed"]);
const Root = styled.div`
  ${createThemedCssVarDefinitions({
  label: {
    light: colors.pepper32,
    dark: colors.salt40
  },
  labelPressed: {
    light: colors.pepper80,
    dark: colors.salt88
  }
})}
`;
const TextLabel = styled.span`
  ${typographyCss.Body3()}
  color: ${cbSemanticCssVars.textPrimary};
`;
const Label = styled.label`
  display: inline-flex;
  align-items: center;

  ${({ "aria-disabled": disabled }) => disabled ? css`
          cursor: not-allowed;

          ${TextLabel} {
            color: ${colorCssVars.label};
          }
        ` : css`
          &:active ${TextLabel} {
            color: ${colorCssVars.labelPressed};
          }
        `}
`;
const Content = styled.span`
  margin-inline-start: 8px;
`;
const Switch = React.forwardRef(
  ({
    checked,
    children,
    className,
    disabled,
    inputProps,
    name,
    onBlur,
    onChange,
    onClick,
    onFocus,
    readOnly,
    required,
    rootRef,
    style,
    value
  }, ref) => /* @__PURE__ */ jsx(Root, { "aria-disabled": disabled, className, ref: rootRef, style, children: /* @__PURE__ */ jsxs(Label, { "aria-disabled": disabled, children: [
    /* @__PURE__ */ jsx(
      InputSwitch,
      {
        ...inputProps,
        checked,
        disabled,
        name,
        onBlur,
        onChange,
        onClick,
        onFocus,
        readOnly,
        ref,
        required,
        value
      }
    ),
    children !== void 0 && /* @__PURE__ */ jsx(Content, { children: typeof children === "string" ? /* @__PURE__ */ jsx(TextLabel, { children }) : children })
  ] }) })
);
Switch.displayName = "Switch";
export {
  InputSwitch,
  Switch
};
//# sourceMappingURL=index.js.map
